import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, X, Volume2, VolumeX, Maximize } from 'lucide-react';
import './Reviews.css';

const VIDEOS = [
    { id: 1, code: 'KH', duration: '0:52', src: 'https://res.cloudinary.com/w067zpm7/video/upload/v1783892896/reviews-1_o3rdxn.mp4' },
    { id: 2, code: 'ТО', duration: '0:44', src: 'https://res.cloudinary.com/w067zpm7/video/upload/v1783893130/reviews-2_cp8upw.mp4' },
    { id: 3, code: 'ОД', duration: '0:58', src: 'https://res.cloudinary.com/w067zpm7/video/upload/v1783893605/reviews-3_s1zi1w.mp4' },
];

const ReviewModal = ({ video, item, onClose }) => {
    const videoRef = useRef(null);
    const [muted, setMuted] = useState(false);
    const [playing, setPlaying] = useState(true);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !videoRef.current.muted;
            setMuted(videoRef.current.muted);
        }
    };

    const togglePlay = () => {
        if (!videoRef.current) return;
        if (videoRef.current.paused) {
            videoRef.current.play();
            setPlaying(true);
        } else {
            videoRef.current.pause();
            setPlaying(false);
        }
    };

    const toggleFullscreen = () => {
        if (videoRef.current?.requestFullscreen) {
            videoRef.current.requestFullscreen();
        } else if (videoRef.current?.webkitEnterFullscreen) {
            // iOS Safari — свой отдельный API для fullscreen на <video>
            videoRef.current.webkitEnterFullscreen();
        }
    };

    return (
        <div className="review-modal" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="review-modal__box" onClick={(e) => e.stopPropagation()}>
                <button className="review-modal__close" onClick={onClose} aria-label="Close">
                    <X size={18} />
                </button>

                <video
                    ref={videoRef}
                    className="review-modal__video"
                    src={video.src}
                    controls={false}
                    autoPlay
                    playsInline
                    onClick={togglePlay}
                />

                <div className="review-modal__overlay">
                    <div className="review-modal__info">
                        <span className="review-modal__name">{item.name}</span>
                        <span className="review-modal__role">
                            {item.role} · {item.location}
                        </span>
                    </div>
                    <div className="review-modal__controls">
                        <button onClick={togglePlay} aria-label="Play/Pause">
                            {playing ? <Pause size={18} /> : <Play size={18} />}
                        </button>
                        <button onClick={toggleMute} aria-label="Mute">
                            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                        </button>
                        <button onClick={toggleFullscreen} aria-label="Fullscreen">
                            <Maximize size={18} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const Reviews = () => {
    const { t } = useTranslation();
    const [active, setActive] = useState(null);

    const items = [1, 2, 3].map((num, i) => ({
        ...VIDEOS[i],
        quote: t(`reviews.items.${num}.quote`),
        name: t(`reviews.items.${num}.name`),
        role: t(`reviews.items.${num}.role`),
        location: t(`reviews.items.${num}.location`),
    }));

    const track = [...items, ...items];

    return (
        <section className="reviews" aria-label={t('reviews.title')}>
            <div className="reviews__head">
                <span className="reviews__eyebrow">{t('reviews.eyebrow')}</span>
                <h2 className="reviews__title">{t('reviews.title')}</h2>
                <p className="reviews__subtitle">{t('reviews.subtitle')}</p>
            </div>

            <div className="reviews__track-wrap">
                <div className="reviews__track">
                    {track.map((item, i) => (
                        <button
                            key={i}
                            className="review-card"
                            onClick={() => setActive(item)}
                            aria-label={`${item.name}, ${item.role}`}
                        >
                            <div className="review-card__media">
                                <video
                                    className="review-card__video"
                                    src={item.src}
                                    muted
                                    loop
                                    autoPlay
                                    playsInline
                                />
                                <span className="review-card__location">{item.location}</span>
                                <span className="review-card__duration">{item.duration}</span>
                                <span className="review-card__play">
                                    <Play size={20} fill="currentColor" />
                                </span>
                                <span className="review-card__avatar">{item.code}</span>
                            </div>

                            <p className="review-card__quote">«{item.quote}»</p>
                            <span className="review-card__author">{item.name}</span>
                            <span className="review-card__meta">{item.role}</span>
                        </button>
                    ))}
                </div>
            </div>

            {active && (
                <ReviewModal video={active} item={active} onClose={() => setActive(null)} />
            )}
        </section>
    );
};

export default Reviews;